import { CommunityListingVisibility, CommunityStatus } from '../enums';
import { ARTICLE_SEO_DESCRIPTION_MAX_CHARS, ARTICLE_SEO_TITLE_MAX_CHARS } from '../entities/article';

export const COMMUNITY_SEO_TITLE_MAX_CHARS = ARTICLE_SEO_TITLE_MAX_CHARS;
export const COMMUNITY_SEO_DESCRIPTION_MAX_CHARS = ARTICLE_SEO_DESCRIPTION_MAX_CHARS;
export const COMMUNITY_SEO_CANONICAL_PATH_MAX_CHARS = 200;
export const COMMUNITY_FEATURED_LIST_MAX = 12;
export const COMMUNITY_SEO_NOINDEX_TABS = [
  'feed',
  'members',
  'map',
  'classroom',
  'calendar',
  'leaderboard',
  'resources',
  'invite',
  'about',
] as const;

export type CommunitySeoNoindexTab = (typeof COMMUNITY_SEO_NOINDEX_TABS)[number];

export type CommunitySeoSource = {
  title: string;
  about?: string | null;
  coverUrl?: string | null;
  logoUrl?: string | null;
  slug?: string | null;
  listingVisibility?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoCanonicalPath?: string | null;
  seoIndexable?: boolean | null;
  seoTwitterTitle?: string | null;
  seoTwitterDescription?: string | null;
  seoNoindexTabs?: string[] | null;
  featuredUntil?: Date | string | null;
};

export type CommunitySeoMetadata = {
  title: string;
  description: string;
  imageUrl: string | null;
  canonicalPath: string | null;
  indexable: boolean;
  twitterTitle: string;
  twitterDescription: string;
  noindexTabs: CommunitySeoNoindexTab[];
  structuredData: Record<string, unknown>;
};

export type CommunityFeaturedCandidate = {
  status: string;
  slug?: string | null;
  listingVisibility?: string | null;
  creatorDisabled?: boolean | null;
  creatorSetupStatus?: string | null;
};

function trimOrNull(value: string | null | undefined, max: number): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

/** Strip HTML/control chars from creator-controlled SEO plain text. */
export function sanitizeCommunitySeoPlainText(
  value: string | null | undefined,
  max: number,
): string | null {
  const trimmed = trimOrNull(value, max);
  if (!trimmed) return null;
  return trimmed.replace(/<[^>]*>/g, '').split('\0').join('');
}

export function normalizeCommunitySeoTitle(value: string | null | undefined): string | null {
  return sanitizeCommunitySeoPlainText(value, COMMUNITY_SEO_TITLE_MAX_CHARS);
}

export function normalizeCommunitySeoDescription(value: string | null | undefined): string | null {
  return sanitizeCommunitySeoPlainText(value, COMMUNITY_SEO_DESCRIPTION_MAX_CHARS);
}

/** Safe JSON-LD serialization for inline script tags (neutralizes </script> breakout). */
export function serializeJsonLdForScriptTag(payload: unknown): string {
  return JSON.stringify(payload).replace(/</g, '\\u003c');
}

export function normalizeCommunitySeoCanonicalPath(
  value: string | null | undefined,
): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('/')) return null;
  return trimmed.slice(0, COMMUNITY_SEO_CANONICAL_PATH_MAX_CHARS);
}

export function excerptCommunityAbout(text: string | null | undefined, max = 160): string {
  return (text ?? '').trim().replace(/\s+/g, ' ').slice(0, max);
}

export function resolveCommunitySeoMetadata(
  community: CommunitySeoSource,
  siteName = 'LOAMIFY',
): CommunitySeoMetadata {
  const fallbackTitle =
    sanitizeCommunitySeoPlainText(community.title, COMMUNITY_SEO_TITLE_MAX_CHARS) || 'Circle';
  const title =
    normalizeCommunitySeoTitle(community.seoTitle) ||
    sanitizeCommunitySeoPlainText(community.title, COMMUNITY_SEO_TITLE_MAX_CHARS) ||
    'Community';
  const description =
    normalizeCommunitySeoDescription(community.seoDescription) ||
    sanitizeCommunitySeoPlainText(
      excerptCommunityAbout(community.about, COMMUNITY_SEO_DESCRIPTION_MAX_CHARS),
      COMMUNITY_SEO_DESCRIPTION_MAX_CHARS,
    ) ||
    `Join ${fallbackTitle} on ${siteName}`;
  const canonicalPath =
    normalizeCommunitySeoCanonicalPath(community.seoCanonicalPath) ??
    (community.slug ? `/circles/${community.slug}` : null);

  const secretListing = community.listingVisibility === CommunityListingVisibility.SECRET;

  return {
    title,
    description,
    imageUrl: community.logoUrl ?? community.coverUrl ?? null,
    canonicalPath,
    indexable: secretListing ? false : community.seoIndexable !== false,
    twitterTitle:
      normalizeCommunitySeoTitle(community.seoTwitterTitle) || title,
    twitterDescription:
      normalizeCommunitySeoDescription(community.seoTwitterDescription) || description,
    noindexTabs: normalizeCommunitySeoNoindexTabs(community.seoNoindexTabs),
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      ...(canonicalPath ? { url: canonicalPath } : {}),
      ...(community.logoUrl || community.coverUrl
        ? {
            primaryImageOfPage: {
              '@type': 'ImageObject',
              url: community.logoUrl ?? community.coverUrl,
            },
          }
        : {}),
      isPartOf: {
        '@type': 'WebSite',
        name: siteName,
      },
    },
  };
}

export function canFeatureCommunityInDiscovery(
  community: CommunityFeaturedCandidate,
): { ok: true } | { ok: false; message: string } {
  if (community.status !== CommunityStatus.PUBLISHED) {
    return { ok: false, message: 'Only published communities can be featured' };
  }
  if (!community.slug?.trim()) {
    return { ok: false, message: 'Published communities must have a slug before featuring' };
  }
  if (
    community.listingVisibility === CommunityListingVisibility.UNLISTED ||
    community.listingVisibility === CommunityListingVisibility.SECRET
  ) {
    return { ok: false, message: 'Unlisted or secret communities cannot be featured in discovery' };
  }
  if (community.creatorDisabled) {
    return { ok: false, message: 'Disabled creators cannot be featured' };
  }
  if (community.creatorSetupStatus && community.creatorSetupStatus !== 'active') {
    return { ok: false, message: 'Only active creators can be featured' };
  }
  return { ok: true };
}

export function normalizeCommunitySeoNoindexTabs(value: unknown): CommunitySeoNoindexTab[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set<string>(COMMUNITY_SEO_NOINDEX_TABS);
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry): entry is CommunitySeoNoindexTab => allowed.has(entry));
}

export function isCommunityTabIndexable(
  tab: string,
  seo: Pick<CommunitySeoMetadata, 'indexable' | 'noindexTabs'>,
): boolean {
  if (!seo.indexable) return false;
  return !seo.noindexTabs.includes(tab as CommunitySeoNoindexTab);
}

export function isCommunityFeaturedActive(
  featuredAt: Date | string | null | undefined,
  featuredUntil: Date | string | null | undefined,
  now = new Date(),
): boolean {
  if (!featuredAt) return false;
  const featuredTimestamp =
    featuredAt instanceof Date ? featuredAt.getTime() : Date.parse(String(featuredAt));
  if (!Number.isFinite(featuredTimestamp) || featuredTimestamp > now.getTime()) return false;
  if (!featuredUntil) return true;
  const untilTimestamp =
    featuredUntil instanceof Date ? featuredUntil.getTime() : Date.parse(String(featuredUntil));
  if (!Number.isFinite(untilTimestamp)) return true;
  return untilTimestamp >= now.getTime();
}

export function buildCommunitySitemapEntries(
  communities: Array<{ slug: string; updatedAt?: Date | string | null }>,
  siteUrl: string,
): Array<{ url: string; lastModified?: string }> {
  const base = siteUrl.replace(/\/+$/, '');
  return communities
    .filter((community) => community.slug?.trim())
    .map((community) => ({
      url: `${base}/circles/${community.slug}`,
      lastModified:
        community.updatedAt instanceof Date
          ? community.updatedAt.toISOString()
          : community.updatedAt ?? undefined,
    }));
}

export function clampCommunityFeaturedLimit(limit: number | null | undefined): number {
  if (limit == null || !Number.isFinite(limit)) return 6;
  return Math.min(Math.max(Math.trunc(limit), 1), COMMUNITY_FEATURED_LIST_MAX);
}
